import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useConfirm } from "../context/ConfirmDialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "./ui/drawer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ArrowUpRight,
} from "lucide-react";

/**
 * CategoryManager — a collapsible accordion drawer to create / rename / delete
 * categories and subcategories (max 2 levels).
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - onChanged: () => void  — called after every mutation so parents can refresh
 */
export default function CategoryManager({ open, onOpenChange, onChanged }) {
  const confirm = useConfirm();
  const [cats, setCats] = useState([]);
  const [expanded, setExpanded] = useState({}); // {parent_id: true}
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [creatingUnder, setCreatingUnder] = useState(null); // null=top-level form hidden; "__root" OR parent_id = form visible
  const [newName, setNewName] = useState("");
  const [newEmoji, setNewEmoji] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/categories");
      setCats(r.data.categories || []);
    } catch (_e) {
      toast.error("Impossibile caricare le categorie");
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const { parents, childrenByParent } = useMemo(() => {
    const parents = cats.filter((c) => !c.parent_id);
    const childrenByParent = {};
    for (const c of cats) {
      if (c.parent_id) {
        childrenByParent[c.parent_id] = childrenByParent[c.parent_id] || [];
        childrenByParent[c.parent_id].push(c);
      }
    }
    parents.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    Object.values(childrenByParent).forEach((arr) =>
      arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
    );
    return { parents, childrenByParent };
  }, [cats]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const startCreate = (parentId) => {
    setCreatingUnder(parentId);
    setNewName("");
    setNewEmoji("");
    if (parentId && parentId !== "__root") {
      setExpanded((e) => ({ ...e, [parentId]: true }));
    }
  };

  const cancelCreate = () => {
    setCreatingUnder(null);
    setNewName("");
    setNewEmoji("");
  };

  const submitCreate = async () => {
    const nm = newName.trim();
    if (!nm) { toast.error("Nome obbligatorio"); return; }
    try {
      await api.post("/categories", {
        name: nm,
        emoji: newEmoji.trim() || null,
        parent_id: creatingUnder && creatingUnder !== "__root" ? creatingUnder : null,
      });
      toast.success("Categoria aggiunta");
      cancelCreate();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creazione fallita");
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.category_id);
    setEditName(cat.name);
    setEditEmoji(cat.emoji || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditEmoji("");
  };

  const submitEdit = async () => {
    const nm = editName.trim();
    if (!nm) { toast.error("Nome obbligatorio"); return; }
    try {
      await api.patch(`/categories/${editingId}`, { name: nm, emoji: editEmoji.trim() || null });
      toast.success("Modificata");
      cancelEdit();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modifica fallita");
    }
  };

  const doDelete = async (cat) => {
    const kids = (childrenByParent[cat.category_id] || []).length;
    const description = kids
      ? `Elimini "${cat.name}" e le sue ${kids} sottocategorie?`
      : `Elimini "${cat.name}"?`;
    const ok = await confirm({
      title: "Elimina categoria",
      description,
      confirmLabel: "Elimina",
      cancelLabel: "Annulla",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/categories/${cat.category_id}`);
      toast.success("Eliminata");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione fallita");
    }
  };

  const moveToTop = async (cat) => {
    try {
      await api.patch(`/categories/${cat.category_id}`, { parent_id: "" });
      toast.success("Spostata in alto");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spostamento fallito");
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="category-manager">
        <div className="mx-auto w-full max-w-md text-zinc-950">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-heading text-2xl font-medium">Gestisci categorie</DrawerTitle>
            <DrawerDescription>Crea, rinomina ed elimina categorie e sottocategorie.</DrawerDescription>
          </DrawerHeader>

          <div className="px-4 pb-6 max-h-[70vh] overflow-y-auto">
            {/* Top-level create */}
            {creatingUnder === "__root" ? (
              <CreateInline
                name={newName}
                emoji={newEmoji}
                onName={setNewName}
                onEmoji={setNewEmoji}
                onCancel={cancelCreate}
                onSubmit={submitCreate}
                testId="cat-new-root"
                placeholder="Nome categoria"
              />
            ) : (
              <button
                onClick={() => startCreate("__root")}
                className="w-full h-11 rounded-xl bg-zinc-950 text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-95"
                data-testid="cat-add-root-btn"
              >
                <Plus className="w-4 h-4" /> Nuova categoria
              </button>
            )}

            <div className="mt-4 space-y-2" data-testid="cat-accordion">
              {parents.map((p) => {
                const kids = childrenByParent[p.category_id] || [];
                const isOpen = !!expanded[p.category_id];
                const isEditing = editingId === p.category_id;
                return (
                  <div key={p.category_id} className="rounded-2xl border border-zinc-200 overflow-hidden" data-testid={`cat-row-${p.category_id}`}>
                    <div className="flex items-center gap-2 px-3 py-3 bg-zinc-50">
                      <button
                        onClick={() => toggle(p.category_id)}
                        className="w-8 h-8 rounded-lg hover:bg-zinc-200 flex items-center justify-center"
                        data-testid={`cat-toggle-${p.category_id}`}
                        aria-label="Espandi"
                      >
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {isEditing ? (
                        <div className="flex-1 flex items-center gap-2">
                          <Input
                            value={editEmoji}
                            onChange={(e) => setEditEmoji(e.target.value.slice(0, 4))}
                            placeholder="😀"
                            className="w-14 h-10 text-center"
                            data-testid={`cat-edit-emoji-${p.category_id}`}
                          />
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="flex-1 h-10"
                            autoFocus
                            data-testid={`cat-edit-name-${p.category_id}`}
                          />
                          <button onClick={submitEdit} className="w-9 h-9 rounded-lg bg-fresh-green text-black flex items-center justify-center" data-testid={`cat-edit-save-${p.category_id}`}>
                            <Check className="w-4 h-4" strokeWidth={3} />
                          </button>
                          <button onClick={cancelEdit} className="w-9 h-9 rounded-lg bg-zinc-200 flex items-center justify-center" data-testid={`cat-edit-cancel-${p.category_id}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0 font-heading text-base font-medium truncate">
                            {p.emoji ? <span className="mr-1">{p.emoji}</span> : null}{p.name}
                            <span className="ml-2 text-[11px] text-zinc-400 font-sans">{kids.length ? `${kids.length} sottocat.` : ""}</span>
                          </div>
                          <button onClick={() => startEdit(p)} className="w-8 h-8 rounded-lg hover:bg-zinc-200 flex items-center justify-center" data-testid={`cat-edit-btn-${p.category_id}`} aria-label="Modifica">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => doDelete(p)} className="w-8 h-8 rounded-lg hover:bg-zinc-200 flex items-center justify-center text-fresh-red-text" data-testid={`cat-delete-btn-${p.category_id}`} aria-label="Elimina">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                    {isOpen && (
                      <div className="bg-white px-3 py-2 space-y-1">
                        {kids.map((k) => (
                          <SubcatRow
                            key={k.category_id}
                            cat={k}
                            editingId={editingId}
                            editName={editName}
                            editEmoji={editEmoji}
                            onEditNameChange={setEditName}
                            onEditEmojiChange={setEditEmoji}
                            onStartEdit={startEdit}
                            onCancelEdit={cancelEdit}
                            onSubmitEdit={submitEdit}
                            onDelete={doDelete}
                            onMoveTop={moveToTop}
                          />
                        ))}
                        {creatingUnder === p.category_id ? (
                          <div className="pl-6">
                            <CreateInline
                              name={newName}
                              emoji={newEmoji}
                              onName={setNewName}
                              onEmoji={setNewEmoji}
                              onCancel={cancelCreate}
                              onSubmit={submitCreate}
                              testId={`cat-new-child-${p.category_id}`}
                              placeholder="Nome sottocategoria"
                              compact
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => startCreate(p.category_id)}
                            className="ml-6 h-8 px-3 rounded-full bg-zinc-100 text-zinc-700 text-xs font-semibold flex items-center gap-1 active:scale-95"
                            data-testid={`cat-add-child-${p.category_id}`}
                          >
                            <Plus className="w-3 h-3" /> Sottocategoria
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {parents.length === 0 && (
                <div className="text-center text-sm text-zinc-500 py-8">Nessuna categoria. Aggiungine una!</div>
              )}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function CreateInline({ name, emoji, onName, onEmoji, onCancel, onSubmit, testId, placeholder, compact }) {
  return (
    <div className={`flex items-center gap-2 ${compact ? "" : "rounded-xl border border-zinc-200 p-2 bg-zinc-50"}`} data-testid={testId}>
      <Input
        value={emoji}
        onChange={(e) => onEmoji(e.target.value.slice(0, 4))}
        placeholder="😀"
        className="w-14 h-10 text-center bg-white"
        data-testid={`${testId}-emoji`}
      />
      <Input
        value={name}
        onChange={(e) => onName(e.target.value)}
        placeholder={placeholder}
        className="flex-1 h-10 bg-white"
        autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
        data-testid={`${testId}-name`}
      />
      <button onClick={onSubmit} className="w-9 h-9 rounded-lg bg-fresh-green text-black flex items-center justify-center active:scale-95" data-testid={`${testId}-save`}>
        <Check className="w-4 h-4" strokeWidth={3} />
      </button>
      <button onClick={onCancel} className="w-9 h-9 rounded-lg bg-zinc-200 flex items-center justify-center active:scale-95" data-testid={`${testId}-cancel`}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function SubcatRow({
  cat,
  editingId,
  editName,
  editEmoji,
  onEditNameChange,
  onEditEmojiChange,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  onDelete,
  onMoveTop,
}) {
  const isEditing = editingId === cat.category_id;
  if (isEditing) {
    return (
      <div className="pl-6 flex items-center gap-2" data-testid={`cat-row-${cat.category_id}`}>
        <Input
          value={editEmoji}
          onChange={(e) => onEditEmojiChange(e.target.value.slice(0, 4))}
          placeholder="😀"
          className="w-14 h-9 text-center"
          data-testid={`cat-edit-emoji-${cat.category_id}`}
        />
        <Input
          value={editName}
          onChange={(e) => onEditNameChange(e.target.value)}
          className="flex-1 h-9"
          autoFocus
          data-testid={`cat-edit-name-${cat.category_id}`}
        />
        <button onClick={onSubmitEdit} className="w-8 h-8 rounded-lg bg-fresh-green text-black flex items-center justify-center" data-testid={`cat-edit-save-${cat.category_id}`}>
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </button>
        <button onClick={onCancelEdit} className="w-8 h-8 rounded-lg bg-zinc-200 flex items-center justify-center" data-testid={`cat-edit-cancel-${cat.category_id}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="pl-6 flex items-center gap-2 py-1.5" data-testid={`cat-row-${cat.category_id}`}>
      <span className="w-2 h-2 rounded-full bg-zinc-300" />
      <div className="flex-1 min-w-0 text-sm truncate">
        {cat.emoji ? <span className="mr-1">{cat.emoji}</span> : null}{cat.name}
      </div>
      <button onClick={() => onStartEdit(cat)} className="w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center" data-testid={`cat-edit-btn-${cat.category_id}`} aria-label="Modifica">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onMoveTop(cat)} className="w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center" data-testid={`cat-movetop-btn-${cat.category_id}`} aria-label="Sposta in alto">
        <ArrowUpRight className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onDelete(cat)} className="w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-fresh-red-text" data-testid={`cat-delete-btn-${cat.category_id}`} aria-label="Elimina">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
